import * as THREE from "three";
import { scene } from "../core";
import { VISUAL_CONFIG } from "@shared/config";
import type { IPlayerState } from "@shared/types";

/**
 * Creates an improved player mesh with better visual details and team indicators
 */
export function createPlayerMesh(player: IPlayerState): THREE.Group {
  // Create a group to hold all player elements
  const group = new THREE.Group();
  group.userData = {
    mouthAngle: 0.5, // Current mouth opening angle factor
    mouthDirection: 0.05, // How fast the mouth opens/closes
    timeOffset: Math.random() * Math.PI * 2, // Random start phase
    teamColor:
      player.team === "A"
        ? new THREE.Color(VISUAL_CONFIG.COLORS.TEAM_A)
        : new THREE.Color(VISUAL_CONFIG.COLORS.TEAM_B),
    teamEmissive:
      player.team === "A"
        ? new THREE.Color(VISUAL_CONFIG.COLORS.TEAM_A_EMISSIVE)
        : new THREE.Color(VISUAL_CONFIG.COLORS.TEAM_B_EMISSIVE),
  };

  // Main body - use icosahedron for better shape than sphere
  const radius = 0.4;

  // Create main body with team-colored texturing instead of a yellow body with red glow
  const bodyGeometry = new THREE.IcosahedronGeometry(radius, 2); // Higher detail level

  // Create material with team color influence
  // Blend the team color with the player yellow color for a more integrated look
  const teamColorInfluence = 0.3; // 30% team color, 70% yellow
  const bodyColor = new THREE.Color(VISUAL_CONFIG.COLORS.PLAYER).lerp(
    player.team === "A"
      ? new THREE.Color(VISUAL_CONFIG.COLORS.TEAM_A)
      : new THREE.Color(VISUAL_CONFIG.COLORS.TEAM_B),
    teamColorInfluence
  );

  // Create material with better shading
  const bodyMaterial = new THREE.MeshStandardMaterial({
    color: bodyColor,
    emissive: VISUAL_CONFIG.COLORS.PLAYER_EMISSIVE,
    emissiveIntensity: 0.6,
    roughness: 0.2, // More polished look
    metalness: 0.2, // Slight metallic sheen
  });

  // Create main body mesh
  const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
  group.add(body);

  // Create mouth with better geometry - use shape with curves instead of cone
  // First create a half-circle shape
  const mouthShape = new THREE.Shape();
  mouthShape.absarc(0, 0, radius * 0.8, 0, Math.PI, false);
  mouthShape.lineTo(0, 0);
  mouthShape.lineTo(radius * 0.8, 0);

  // Extrude the shape to create 3D mouth
  const mouthGeometry = new THREE.ExtrudeGeometry(mouthShape, {
    depth: radius * 0.3,
    bevelEnabled: true,
    bevelSegments: 3,
    bevelSize: radius * 0.05,
    bevelThickness: radius * 0.05,
  });

  // Use black material for mouth interior
  const mouthMaterial = new THREE.MeshStandardMaterial({
    color: 0x000000,
    roughness: 1.0,
    metalness: 0.0,
    side: THREE.DoubleSide,
  });

  // Create mouth mesh
  const mouth = new THREE.Mesh(mouthGeometry, mouthMaterial);

  // Position the mouth in front
  mouth.rotation.x = Math.PI / 2;
  mouth.position.set(0, 0, radius * 0.7);

  // Store original mouth state for animation
  mouth.userData = {
    originalScale: mouth.scale.clone(),
    originalRotation: mouth.rotation.clone(),
  };

  group.add(mouth);

  // Add a team-colored racing stripe around the equator for clear team indication
  const addTeamStripe = () => {
    const stripeGeometry = new THREE.TorusGeometry(radius * 1.01, 0.05, 16, 32);
    const stripeMaterial = new THREE.MeshStandardMaterial({
      color:
        player.team === "A"
          ? VISUAL_CONFIG.COLORS.TEAM_A
          : VISUAL_CONFIG.COLORS.TEAM_B,
      emissive:
        player.team === "A"
          ? VISUAL_CONFIG.COLORS.TEAM_A_EMISSIVE
          : VISUAL_CONFIG.COLORS.TEAM_B_EMISSIVE,
      emissiveIntensity: 0.8,
      roughness: 0.3,
      metalness: 0.6,
    });

    const stripe = new THREE.Mesh(stripeGeometry, stripeMaterial);
    stripe.rotation.x = Math.PI / 2;
    group.add(stripe);

    return stripe;
  };

  addTeamStripe();

  // Add team letter on top
  // Create team emblem - A or B that sits on top
  const createTeamEmblem = (team: "A" | "B") => {
    // Create the letter geometry
    const letter = team === "A" ? createLetterA() : createLetterB();
    letter.position.y = radius * 0.95;
    letter.rotation.x = -Math.PI / 2; // Make it face up
    return letter;
  };

  const createLetterA = () => {
    const shape = new THREE.Shape();
    const width = radius * 0.3;
    const height = radius * 0.45;

    // Draw an A
    shape.moveTo(0, height);
    shape.lineTo(width / 2, 0);
    shape.lineTo(width, height);
    shape.lineTo(width * 0.85, height);
    shape.lineTo(width * 0.75, height * 0.7);
    shape.lineTo(width * 0.25, height * 0.7);
    shape.lineTo(width * 0.15, height);
    shape.lineTo(0, height);

    // Create a hole for the middle of the A
    const hole = new THREE.Path();
    hole.moveTo(width * 0.35, height * 0.5);
    hole.lineTo(width * 0.5, height * 0.25);
    hole.lineTo(width * 0.65, height * 0.5);
    hole.lineTo(width * 0.35, height * 0.5);
    shape.holes.push(hole);

    const geometry = new THREE.ExtrudeGeometry(shape, {
      depth: 0.03,
      bevelEnabled: true,
      bevelSegments: 2,
      bevelSize: 0.01,
      bevelThickness: 0.01,
    });

    const material = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      emissive:
        player.team === "A"
          ? VISUAL_CONFIG.COLORS.TEAM_A_EMISSIVE
          : VISUAL_CONFIG.COLORS.TEAM_B_EMISSIVE,
      emissiveIntensity: 0.7,
      roughness: 0.3,
      metalness: 0.9,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.x = -width / 2; // Center the letter

    // Add a small point light to make the letter glow
    const letterLight = new THREE.PointLight(
      player.team === "A"
        ? VISUAL_CONFIG.COLORS.TEAM_A
        : VISUAL_CONFIG.COLORS.TEAM_B,
      0.4,
      0.3
    );
    letterLight.position.y = 0.05;
    mesh.add(letterLight);

    return mesh;
  };

  const createLetterB = () => {
    const shape = new THREE.Shape();
    const width = radius * 0.3;
    const height = radius * 0.45;

    // Draw a B
    shape.moveTo(0, 0);
    shape.lineTo(0, height);
    shape.lineTo(width * 0.7, height);
    shape.bezierCurveTo(
      width,
      height,
      width,
      height * 0.7,
      width * 0.7,
      height * 0.6
    );
    shape.bezierCurveTo(
      width * 0.9,
      height * 0.5,
      width * 0.9,
      height * 0.1,
      width * 0.7,
      0
    );
    shape.lineTo(0, 0);

    // Add holes for the counter spaces in the B
    // Top hole
    const hole1 = new THREE.Path();
    hole1.moveTo(width * 0.2, height * 0.6);
    hole1.lineTo(width * 0.2, height * 0.8);
    hole1.lineTo(width * 0.5, height * 0.8);
    hole1.bezierCurveTo(
      width * 0.7,
      height * 0.8,
      width * 0.7,
      height * 0.6,
      width * 0.5,
      height * 0.6
    );
    hole1.lineTo(width * 0.2, height * 0.6);
    shape.holes.push(hole1);

    // Bottom hole
    const hole2 = new THREE.Path();
    hole2.moveTo(width * 0.2, height * 0.2);
    hole2.lineTo(width * 0.2, height * 0.4);
    hole2.lineTo(width * 0.55, height * 0.4);
    hole2.bezierCurveTo(
      width * 0.75,
      height * 0.4,
      width * 0.75,
      height * 0.2,
      width * 0.55,
      height * 0.2
    );
    hole2.lineTo(width * 0.2, height * 0.2);
    shape.holes.push(hole2);

    const geometry = new THREE.ExtrudeGeometry(shape, {
      depth: 0.03,
      bevelEnabled: true,
      bevelSegments: 2,
      bevelSize: 0.01,
      bevelThickness: 0.01,
    });

    const material = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      emissive:
        player.team === "A"
          ? VISUAL_CONFIG.COLORS.TEAM_A_EMISSIVE
          : VISUAL_CONFIG.COLORS.TEAM_B_EMISSIVE,
      emissiveIntensity: 0.7,
      roughness: 0.3,
      metalness: 0.9,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.x = -width / 2; // Center the letter

    // Add a small point light to make the letter glow
    const letterLight = new THREE.PointLight(
      player.team === "A"
        ? VISUAL_CONFIG.COLORS.TEAM_A
        : VISUAL_CONFIG.COLORS.TEAM_B,
      0.4,
      0.3
    );
    letterLight.position.y = 0.05;
    mesh.add(letterLight);

    return mesh;
  };

  // Add team emblem
  const teamLetter = createTeamEmblem(player.team);
  group.add(teamLetter);

  // Add light trail effect with team colors
  const addLightTrail = () => {
    // Create a few point lights that follow behind
    const trailColors =
      player.team === "A" ? [0xff5500, 0xffaa00] : [0x0055ff, 0x00aaff];

    for (let i = 0; i < 2; i++) {
      const trailLight = new THREE.PointLight(
        trailColors[i % trailColors.length],
        0.4,
        1.2
      );
      trailLight.position.set(
        Math.random() * 0.1 - 0.05,
        0.2,
        -radius - 0.1 - i * 0.1
      );

      // Store original position
      trailLight.userData = {
        originalPosition: trailLight.position.clone(),
        offset: Math.random() * Math.PI * 2,
        speed: 0.01 + Math.random() * 0.01,
      };

      group.add(trailLight);
    }
  };

  addLightTrail();

  // Subtle glow effect - much more subtle than before
  const createSubtleGlow = () => {
    // Light glow - the goal is for this to be barely visible
    const glowGeometry = new THREE.SphereGeometry(radius * 1.1, 24, 24);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: VISUAL_CONFIG.COLORS.PLAYER,
      transparent: true,
      opacity: 0.1, // Very subtle
      side: THREE.BackSide,
    });

    const glow = new THREE.Mesh(glowGeometry, glowMaterial);

    // Very small point light for overall illumination
    const glowLight = new THREE.PointLight(0xffffcc, 0.3, 1);

    return { glow, glowLight };
  };

  // Add subtle glow
  const glowEffects = createSubtleGlow();
  group.add(glowEffects.glow);
  group.add(glowEffects.glowLight);

  // Add rocket indicator if player has rocket
  if (player.hasRocket) {
    import("./rocketModel").then(({ createRocketIndicator }) => {
      const rocketIndicator = createRocketIndicator();
      rocketIndicator.position.set(0, -radius * 0.8, 0);
      rocketIndicator.name = "rocketIndicator";
      group.add(rocketIndicator);
    });
  }

  // Position player exactly in the center of the cell
  // Ensure y position is consistent to avoid wall clipping issues
  const cellX = Math.floor(player.x);
  const cellY = Math.floor(player.y);
  const offsetX = player.x - cellX - 0.5; // Offset from center of cell
  const offsetY = player.y - cellY - 0.5; // Offset from center of cell

  // Limit how far from center entity can be to prevent wall clipping
  const maxOffset = 0.4; // Maximum offset from center (prevents wall clipping)
  const clampedOffsetX = Math.max(-maxOffset, Math.min(maxOffset, offsetX));
  const clampedOffsetY = Math.max(-maxOffset, Math.min(maxOffset, offsetY));

  group.position.set(
    cellX + 0.5 + clampedOffsetX, // Center of cell X + limited offset
    0.4, // Standard height
    cellY + 0.5 + clampedOffsetY // Center of cell Y + limited offset
  );

  group.castShadow = true;
  group.receiveShadow = true;
  scene.add(group);

  return group;
}

/**
 * Animates the player mesh
 */
export function animatePlayer(
  playerMesh: THREE.Group,
  time: number,
  isMoving = true
): void {
  if (!playerMesh || !playerMesh.userData) return;

  const ud = playerMesh.userData;

  // Animate mouth chomping
  if (isMoving) {
    ud.mouthAngle += ud.mouthDirection;

    // Reverse direction when mouth is fully open or closed
    if (ud.mouthAngle > 1.0 || ud.mouthAngle < 0.1) {
      ud.mouthDirection *= -1;
    }

    // Find mouth mesh (second child)
    if (playerMesh.children.length > 1) {
      const mouth = playerMesh.children[1];
      if (mouth?.scale) {
        mouth.scale.z = 1.0 - ud.mouthAngle * 0.8;
      }
    }
  }

  // Find team stripe and animate rotation
  for (let i = 0; i < playerMesh.children.length; i++) {
    const child = playerMesh.children[i];
    // Check if this is the team stripe (the torus geometry)
    if (
      child instanceof THREE.Mesh &&
      child.geometry?.type === "TorusGeometry"
    ) {
      child.rotation.z = time * 0.001; // Slowly rotate
      break;
    }
  }

  // Animate trail lights
  for (const child of playerMesh.children) {
    if (child.type === "PointLight" && child.userData?.originalPosition) {
      const light = child as THREE.PointLight;
      const op = light.userData.originalPosition;
      // Make trail lights flicker and wiggle
      light.position.set(
        op.x + Math.sin(time * 0.01 + light.userData.offset) * 0.03,
        op.y + Math.cos(time * 0.01 + light.userData.offset) * 0.02,
        op.z
      );

      // Random intensity flicker
      light.intensity = 0.3 + Math.sin(time * light.userData.speed) * 0.15;
    }
  }
}

/**
 * Updates the player's mouth direction based on movement
 */
export function updatePlayerMouthDirection(
  playerMesh: THREE.Group,
  direction: number
): void {
  if (!playerMesh) return;

  // Find the mouth (usually the second child)
  let mouth = null;
  for (let i = 0; i < playerMesh.children.length; i++) {
    const child = playerMesh.children[i];
    if (
      child instanceof THREE.Mesh &&
      child.geometry?.type === "ExtrudeGeometry"
    ) {
      mouth = child;
      break;
    }
  }

  if (!mouth) return;

  // Set rotation based on direction
  switch (direction) {
    case 0: // North
      playerMesh.rotation.y = Math.PI;
      break;
    case 1: // East
      playerMesh.rotation.y = Math.PI / 2;
      break;
    case 2: // South
      playerMesh.rotation.y = 0;
      break;
    case 3: // West
      playerMesh.rotation.y = -Math.PI / 2;
      break;
  }
}
